import { useCallback, useEffect, useRef, useState } from 'react';

// 공통 fetch lifecycle 헬퍼.
// 각 도메인 hook은 이 위에서 thin wrapper만 만든다.
// shape: { data, loading, error, reload }.

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  reload: () => void;
  // 서버 재조회 없이 로컬 데이터만 갱신 — 낙관적 업데이트/부분 교체용.
  mutate: (updater: (prev: T | null) => T | null) => void;
}

// fetcher가 AbortSignal을 받도록 강제 — unmount 시 in-flight 정리.
export type Fetcher<T> = (signal: AbortSignal) => Promise<T>;

export function useFetch<T>(
  fetcher: Fetcher<T>,
  deps: ReadonlyArray<unknown>,
  options: { enabled?: boolean } = {},
): AsyncState<T> {
  const enabled = options.enabled ?? true;
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  // bump 카운터로 강제 재로드
  const [tick, setTick] = useState(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    if (!enabled) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    const ctrl = new AbortController();
    let alive = true;
    setLoading(true);
    setError(null);
    fetcherRef
      .current(ctrl.signal)
      .then((res) => {
        if (!alive) return;
        setData(res);
        setLoading(false);
      })
      .catch((err) => {
        if (!alive) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      });
    return () => {
      alive = false;
      ctrl.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick, enabled]);

  const reload = useCallback(() => {
    setTick((n) => n + 1);
  }, []);

  const mutate = useCallback((updater: (prev: T | null) => T | null) => {
    setData((prev) => updater(prev));
  }, []);

  return { data, loading, error, reload, mutate };
}
